import { compact, keyBy, map } from 'lodash'
import { useMemo } from 'react'

import { useCurrentOrgId } from '~/store/slices/currentOrg'

import type { ChargePortModel, GetVehiclesForOrganizationArgs, VehicleAuthTagModel, VehicleModel } from '../client/fleet-management'
import { useGetLatestVehicleStatusesQuery, useGetVehiclesForOrganizationV1Query } from '../client/fleet-management'
import { useManagedVehicles } from '../manager'
import type { UsePollingOptions } from './polling'
import { usePolling } from './polling'
import { useVehicleHomeSiteFilterCallback } from './useHomeSiteFiltering'
import { combineSkip, useNormalizedLookup, useTransformedDict } from './utils'

/******************************************************************************
 * GET /organizations/{orgId}/vehicles
 ******************************************************************************/
type UseVehiclesInput = {
  args?: GetVehiclesForOrganizationArgs
  options?: UsePollingOptions
  /**
   * Include logically-deleted vehicles.
   * - Fetch layer (`usePolledVehicles`): request deleted vehicles from the API so the store holds them.
   * - Read layer (`useVehicles` etc.): skip the default `logicalDeleteIn` exclude filter.
   * Defaults to false so live surfaces (page/map/dropdowns) never show deleted vehicles.
   */
  includeDeleted?: boolean
}

export function usePolledVehicles({ args, options, includeDeleted }: UseVehiclesInput = {}) {
  const currentOrgId = useCurrentOrgId()
  const orgId = args?.id ?? currentOrgId

  // Split into multiple pages to handle large datasets
  const resultPage1 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 0, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage2 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 1, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage3 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 2, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage4 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 3, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage5 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 4, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage6 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 5, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage7 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 6, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage8 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 7, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage9 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 8, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  const resultPage10 = usePolling(
    useGetVehiclesForOrganizationV1Query,
    { id: orgId, page: 9, count: 1000, includeDeleted, ...args },
    combineSkip(!orgId, options)
  )

  // Combine results only when all pages are successfully fetched.
  // Depend on each page's `data` (stable across non-data-change refetches / isFetching
  // flips) rather than the full result wrapper, so this memo doesn't invalidate every
  // poll cycle and force downstream consumers to re-render needlessly.
  const combinedVehicles = useMemo(() => {
    if (
      resultPage1.isSuccess &&
      resultPage2.isSuccess &&
      resultPage3.isSuccess &&
      resultPage4.isSuccess &&
      resultPage5.isSuccess &&
      resultPage6.isSuccess &&
      resultPage7.isSuccess &&
      resultPage8.isSuccess &&
      resultPage9.isSuccess &&
      resultPage10.isSuccess
    ) {
      return [
        ...resultPage1.data.data,
        ...resultPage2.data.data,
        ...resultPage3.data.data,
        ...resultPage4.data.data,
        ...resultPage5.data.data,
        ...resultPage6.data.data,
        ...resultPage7.data.data,
        ...resultPage8.data.data,
        ...resultPage9.data.data,
        ...resultPage10.data.data
      ]
    }
    return null
  }, [
    resultPage1.data,
    resultPage2.data,
    resultPage3.data,
    resultPage4.data,
    resultPage5.data,
    resultPage6.data,
    resultPage7.data,
    resultPage8.data,
    resultPage9.data,
    resultPage10.data,
    resultPage1.isSuccess,
    resultPage2.isSuccess,
    resultPage3.isSuccess,
    resultPage4.isSuccess,
    resultPage5.isSuccess,
    resultPage6.isSuccess,
    resultPage7.isSuccess,
    resultPage8.isSuccess,
    resultPage9.isSuccess,
    resultPage10.isSuccess
  ])

  // Combine cached data from currentData (last successful result for each page)
  const cachedVehicles = useMemo(() => {
    const allPages = [
      resultPage1,
      resultPage2,
      resultPage3,
      resultPage4,
      resultPage5,
      resultPage6,
      resultPage7,
      resultPage8,
      resultPage9,
      resultPage10
    ]
    return allPages.flatMap((page) => page.currentData?.data ?? [])
    // We only care about recomputing when the currentData changes, not the whole query result object.
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    resultPage1.currentData,
    resultPage2.currentData,
    resultPage3.currentData,
    resultPage4.currentData,
    resultPage5.currentData,
    resultPage6.currentData,
    resultPage7.currentData,
    resultPage8.currentData,
    resultPage9.currentData,
    resultPage10.currentData
  ])

  const pages = [
    resultPage1,
    resultPage2,
    resultPage3,
    resultPage4,
    resultPage5,
    resultPage6,
    resultPage7,
    resultPage8,
    resultPage9,
    resultPage10
  ]
  const isLoading = pages.some((page) => page.isLoading)
  const isFetching = pages.some((page) => page.isFetching)

  // When useCache is true, prefer cached data.
  // Cached reference will update as fresh data loads.
  const { useCache } = options ?? {}
  const effectiveVehicles = useCache && cachedVehicles.length > 0 ? cachedVehicles : combinedVehicles

  // When useCache is true, only show loading if there's no cached data
  const effectiveIsLoading = useCache ? isLoading && cachedVehicles.length === 0 : isLoading

  const filter = useVehicleHomeSiteFilterCallback()
  const vehicles = useMemo(() => effectiveVehicles?.filter(filter), [effectiveVehicles, filter])

  return { vehicles, isLoading: effectiveIsLoading, isFetching }
}

export function useVehicles({ args, options, includeDeleted }: UseVehiclesInput = {}) {
  return useManagedVehicles({ filters: { orgId: args?.id }, options, includeDeleted })
}

export function useOrgVehicles(orgId?: string, options?: UsePollingOptions) {
  return useVehicles({
    args: { id: orgId ?? '' },
    options: combineSkip(!orgId, options)
  })
}

export function useSiteVehicles(siteId?: string, options?: { intervalSeconds?: number }) {
  return useManagedVehicles({ filters: { siteId }, options })
}

export function useAtHomeVehicles(siteId: string) {
  const { vehicles } = useSiteVehicles(siteId)
  return useMemo(() => {
    return vehicles.filter((vehicle) => {
      return vehicle.currentLocation?.locationOfInterestModels?.some((loi) => loi.id === siteId)
    })
  }, [vehicles, siteId])
}

export function useOrgVehiclesMap(input?: UseVehiclesInput) {
  const { isLoading, isFetching, vehicles } = useVehicles(input)
  const orgVehicles = useMemo(() => keyBy(vehicles ?? [], 'id'), [vehicles])

  return {
    orgVehicles,
    isLoading,
    isFetching,
    lookup: (vehicleId?: string) => (vehicleId ? orgVehicles[vehicleId] : undefined)
  }
}

const AUTH_TAG_PREFIXES = ['VID:', 'EVCCID:']

/**
 * Returns a lookup of auth tag -> array of vehicles that have ever had that tag.
 * This preserves historical mappings and allows downstream time-aware selection.
 */
export function useVehiclesByAllAuthTagsMap(vehiclesInput?: VehicleModel[]) {
  // Fetch vehicles if they are not provided. Include deleted vehicles so historical
  // transactions (whose auth tag may belong to a since-deleted vehicle) still resolve a name.
  const { vehicles: vehiclesFetched, ...result } = useVehicles({ options: { skip: !!vehiclesInput }, includeDeleted: true })
  const vehicles = vehiclesInput ?? vehiclesFetched

  const withAllAuthTags = useMemo(() => {
    // Only include vehicles that have at least one auth tag
    const filteredVehicles = vehicles.filter((v) => !!v.vehicleAuthTags?.length)

    // Build a dictionary with pre-normalized keys to ensure vehicles with the same normalized tag
    return filteredVehicles.reduce((acc: Record<string, VehicleModel[]>, vehicle) => {
      const tags = compact(map(vehicle.vehicleAuthTags, 'authTag'))
      tags.forEach((tag) => {
        const normalizedTag = normalizeAuthTag(tag)
        if (!normalizedTag) return

        const existing = acc[normalizedTag]
        if (existing) {
          // Only add if not already in the array (avoid duplicates)
          if (!existing.some((v) => v.id === vehicle.id)) {
            existing.push(vehicle)
          }
        } else {
          acc[normalizedTag] = [vehicle]
        }
      })
      return acc
    }, {})
  }, [vehicles])

  // Also expose a by-id map so callers can resolve a transaction's stamped vehicle id
  // (authTagMapping.vehicleId) directly, independent of the time-aware tag lookup (ENG-9737).
  const vehiclesById = useMemo(() => keyBy(vehicles, 'id'), [vehicles])

  return {
    vehiclesByAllAuthTags: useTransformedDict(withAllAuthTags, {
      caseInsensitive: true,
      prefix: AUTH_TAG_PREFIXES,
      separator: ':'
    }),
    vehiclesById,
    ...result
  }
}

/**
 * Normalize an auth tag for comparison and lookup by removing known prefixes, uppercasing, and removing colons.
 */
function normalizeAuthTag(tag?: string): string | undefined {
  if (!tag) return undefined
  // Remove any leading or trailing whitespace and convert to uppercase
  let key = tag.trim().toUpperCase()
  // Remove any leading 'VID:' or 'EVCCID:' prefixes
  if (key.startsWith('VID:')) key = key.slice(4)
  else if (key.startsWith('EVCCID:')) key = key.slice(7)
  // Remove any colons
  key = key.replace(/:/g, '')
  // Return the normalized tag
  return key
}

/**
 * Helper to check if a string contains only alphanumeric characters (no special chars like dashes)
 */
function isAlphanumeric(str: string): boolean {
  return /^[A-Z0-9]+$/i.test(str)
}

/**
 * Creates all normalized variants of an auth tag for querying transactions.
 * This matches the backend logic to ensure we query all possible tag representations.
 *
 * Variants include:
 * - The original auth tag
 * - The normalized version (if different from original case-insensitively)
 * - The "VID:" prefixed version of the normalized tag (if <= 20 chars)
 * - The "EVCCID:" prefixed version of the normalized tag (if <= 20 chars)
 * - The normalized tag with colon separators between octets (if even length, alphanumeric, and <= 20 chars)
 *
 * @param authTag The authentication tag to generate variants for
 * @returns Array of unique tag variants
 */
export function getAuthTagVariants(authTag: string): string[] {
  const variants = new Set<string>()

  // Add original tag
  variants.add(authTag)

  const normalized = normalizeAuthTag(authTag)
  if (!normalized) return [authTag]

  // Add normalized tag if different from original (case-insensitive comparison)
  if (authTag.toUpperCase() !== normalized) {
    variants.add(normalized)
  }

  // Add VID: prefixed normalized tag if it doesn't exceed 20 characters and is not a duplicate
  const vidPrefixed = `VID:${normalized}`
  if (vidPrefixed.length <= 20 && authTag.toUpperCase() !== vidPrefixed) {
    variants.add(vidPrefixed)
  }

  // Add EVCCID: prefixed normalized tag if it doesn't exceed 20 characters and is not a duplicate
  const evccidPrefixed = `EVCCID:${normalized}`
  if (evccidPrefixed.length <= 20 && authTag.toUpperCase() !== evccidPrefixed) {
    variants.add(evccidPrefixed)
  }

  // Add normalized tag with colon separators between octets if:
  // - normalized tag has even number of characters
  // - normalized tag is alphanumeric (no special characters like dashes)
  // - adding colons doesn't exceed 20 characters
  // - is not a duplicate of raw tag
  if (normalized.length % 2 === 0 && normalized.length > 0 && isAlphanumeric(normalized)) {
    const colonSeparated = normalized.match(/.{1,2}/g)?.join(':') ?? ''
    if (colonSeparated.length <= 20 && authTag.toUpperCase() !== colonSeparated.toUpperCase()) {
      variants.add(colonSeparated)
    }
  }

  return Array.from(variants)
}

/**
 * Utility function to compare two auth tags for equality, normalizing the tags first.
 * @param tag1
 * @param tag2
 * @returns true if the tags match, false otherwise
 */
function tagsMatch(tag1: string, tag2: string): boolean {
  return normalizeAuthTag(tag1) === normalizeAuthTag(tag2)
}

/**
 * Returns true if the provided tag was active for the given vehicle at the provided timestamp.
 * A missing validTo is treated as open-ended.
 */
export function isTagActiveForVehicleAtTime(vehicle: VehicleModel, tag: string, timestampMs: number): boolean {
  const authTags = vehicle.vehicleAuthTags ?? []

  // Iterate over all auth tags for the vehicle
  for (const vehicleTag of authTags) {
    // If the tags don't match, continue to the next tag
    if (!tagsMatch(vehicleTag.authTag, tag)) continue

    // If the tag does match, check if it was active at the given timestamp
    const fromMs = Date.parse(vehicleTag.validFrom)
    if (Number.isNaN(fromMs)) continue
    const toMs = vehicleTag.validTo ? Date.parse(vehicleTag.validTo) : Number.POSITIVE_INFINITY
    if (timestampMs >= fromMs && timestampMs < toMs) return true
  }
  return false
}

/**
 * Pure helper to select the single vehicle that had authTag active at timestamp.
 */
export function findVehicleByAuthTagAtTime(
  vehiclesByAllAuthTags: Record<string, VehicleModel[]>,
  authTag: string | undefined,
  timestampMs: number | undefined
): VehicleModel | undefined {
  if (!authTag || !timestampMs) return undefined
  const normalized = normalizeAuthTag(authTag)
  if (!normalized) return undefined
  const candidates = vehiclesByAllAuthTags[normalized]
  if (!candidates?.length) return undefined
  for (const vehicle of candidates) {
    if (isTagActiveForVehicleAtTime(vehicle, authTag, timestampMs)) return vehicle
  }
  return undefined
}

/**
 * Resolve the vehicle for a transaction (ENG-9737).
 *
 * Prefers the vehicle id chargers-api stamps on the transaction
 * (`authTagMapping.vehicleId`) — frozen at charge time, so it survives the vehicle
 * being offboarded/re-onboarded and its auth tag reassigned — resolved against the
 * include-deleted `vehiclesById` map. Falls back to the time-aware auth-tag lookup
 * when there's no stamped mapping (older transactions, ENG-9005) or the mapped
 * vehicle isn't loaded.
 */
export function resolveTransactionVehicle(
  txn: { ocppTag?: string; authTagMapping?: { vehicleId?: string } },
  timestampMs: number | undefined,
  vehiclesById: Record<string, VehicleModel>,
  vehiclesByAllAuthTags: Record<string, VehicleModel[]>
): VehicleModel | undefined {
  const mappedVehicleId = txn.authTagMapping?.vehicleId
  if (mappedVehicleId) {
    const vehicle = vehiclesById[mappedVehicleId]
    if (vehicle) return vehicle
  }
  return findVehicleByAuthTagAtTime(vehiclesByAllAuthTags, txn.ocppTag, timestampMs)
}

/**
 * This hook maps vehicles by their charge port ID. This is useful when you need to look up vehicles
 * by Ports that don't have an auth tag assigned, for example when mapping a new auth tag to a vehicle.
 */
export function useVehicleMapByPortId(vehiclesInput?: VehicleModel[]) {
  // Fetch vehicles if they are not provided
  const { vehicles: vehiclesFetched, ...result } = useVehicles({ options: { skip: !!vehiclesInput } })
  const vehicles = vehiclesInput ?? vehiclesFetched

  const mapByPortId = useMemo(() => {
    const filteredVehicles = vehicles.filter((v) => !!v.chargePorts?.length)
    return filteredVehicles.reduce((acc: Record<string, VehicleModel>, vehicle) => {
      const portIds = map(vehicle.chargePorts, 'id')
      portIds.forEach((id) => (acc[id] = vehicle))
      return acc
    }, {})
  }, [vehicles])

  return {
    vehicleMapByPortId: useTransformedDict(mapByPortId, { caseInsensitive: true }),
    ...result
  }
}

const VEHICLE_PORT_LOOKUP_OPTIONS = {
  caseInsensitive: true,
  prefix: AUTH_TAG_PREFIXES,
  separator: ':',
  getKey: (tag: ChargePortModel | VehicleAuthTagModel) => tag.authTag
}

/**
 * Returns normalized lookup object  to find a vehicle Charge Port by auth tag.
 * The lookup is case-insensitive and ignores 'VID:' or 'EVCCID:' prefixes.
 *
 * @returns NormalizedLookupResult
 */
export function useVehiclePortByAuthTag(active = true, vehiclesInput?: VehicleModel[]) {
  // Fetch vehicles if they are not provided
  const { vehicles: vehiclesFetched } = useVehicles({ options: { skip: !!vehiclesInput } })
  const vehicles = vehiclesInput ?? vehiclesFetched

  const portsOrAuthTags = useMemo(() => {
    return vehicles.flatMap<ChargePortModel | VehicleAuthTagModel>((vehicle) => {
      return (active ? vehicle.chargePorts : vehicle.vehicleAuthTags) ?? []
    })
  }, [vehicles, active])

  return useNormalizedLookup(portsOrAuthTags, VEHICLE_PORT_LOOKUP_OPTIONS)
}

/******************************************************************************
 * GET /vehicles/{vehicleId}
 ******************************************************************************/
type UseVehicleInput = {
  args?: { id?: string }
  options?: UsePollingOptions
}

export function useVehicle({ args, options }: UseVehicleInput = {}) {
  const indexedDbVehicles = useManagedVehicles({ options })

  return useMemo(() => {
    const { vehicles, ...rest } = indexedDbVehicles
    return { ...rest, vehicle: vehicles.find((v) => v.id === args?.id) }
  }, [indexedDbVehicles, args?.id])
}

/******************************************************************************
 * GET /api/vehicles/latest-statuses
 ******************************************************************************/
/**
 * Polls for latest vehicle status data on a 5 second basis.
 */
export function usePolledVehicleLatestStatuses(siteId?: string, options?: UsePollingOptions) {
  const result = usePolling(
    useGetLatestVehicleStatusesQuery,
    { siteId: siteId ?? '' },
    combineSkip(!siteId, { intervalSeconds: 5, ...options })
  )

  return { vehicleStatuses: result.data, ...result }
}
